import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, XCircle, Home } from 'lucide-react';
import { Button } from '../../components/ui/button';
import PublicLayout from '../../components/shared/PublicLayout';
import axios from 'axios';
import { useCart } from '../../contexts/CartContext';
import { getApiBase } from '../../lib/api';

const API = getApiBase();

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentReference = searchParams.get('payment_reference');
  const orderId = searchParams.get('order_id');
  const paymentMethod = searchParams.get('payment_method');
  const [status, setStatus] = useState('checking');
  const { clearCart } = useCart();

  useEffect(() => {
    clearCart();
    const paymentLookup = sessionId || paymentReference;
    if (paymentLookup) {
      let attempts = 0;
      const poll = async () => {
        if (attempts >= 5) { setStatus('timeout'); return; }
        try {
          const { data } = await axios.get(`${API}/api/payments/status/${paymentLookup}`);
          if (data.payment_status === 'paid') { setStatus('paid'); return; }
          if (data.payment_status === 'expired' || data.payment_status === 'failed') { setStatus('failed'); return; }
          attempts++;
          setTimeout(poll, 2000);
        } catch { setStatus('error'); }
      };
      poll();
    } else if (orderId) {
      setStatus('submitted');
    } else {
      setStatus('submitted');
    }
  }, [sessionId, paymentReference, orderId, clearCart]);

  const icons = {
    checking: <Clock className="w-16 h-16 text-[#D4AF37] animate-pulse" />,
    paid: <CheckCircle className="w-16 h-16 text-[#5B6B3F]" />,
    submitted: <CheckCircle className="w-16 h-16 text-[#5B6B3F]" />,
    failed: <XCircle className="w-16 h-16 text-[#8C3A3A]" />,
    timeout: <Clock className="w-16 h-16 text-[#D4AF37]" />,
    error: <XCircle className="w-16 h-16 text-[#8C3A3A]" />,
  };

  const messages = {
    checking: { title: 'Verification du paiement...', desc: 'Veuillez patienter.' },
    paid: { title: 'Paiement confirme !', desc: 'Votre commande est en cours de preparation.' },
    submitted: { title: 'Commande envoyee !', desc: 'Votre commande est en attente de validation.' },
    failed: { title: 'Paiement echoue', desc: 'Le paiement n\'a pas abouti. Veuillez reessayer.' },
    timeout: { title: 'Verification en cours', desc: 'Votre paiement est en cours de traitement.' },
    error: { title: 'Erreur', desc: 'Une erreur est survenue.' },
  };

  const msg = messages[status] || messages.error;
  const paymentMethodLabels = {
    cash: 'Argent liquide',
    card: 'Carte bleue en ligne',
    ticket_restaurant: 'Ticket restaurant',
    other: 'Carte bancaire a la reception',
    card_on_receipt: 'Carte bancaire a la reception',
  };

  return (
    <PublicLayout>
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="checkout-success-page">
        <div className="mb-6 flex justify-center">{icons[status]}</div>
        <h1 className="font-heading text-3xl font-bold text-[#2A2A28] mb-4">{msg.title}</h1>
        <p className="text-[#7A7A75] mb-8">{msg.desc}</p>
        {!sessionId && !paymentReference && paymentMethod && (
          <div className="mb-8 rounded-2xl border border-[#D4AF37]/20 bg-[#D4AF37]/10 px-4 py-4 text-sm text-[#7A5B12]">
            Paiement prevu: <span className="font-semibold">{paymentMethodLabels[paymentMethod] || paymentMethod}</span>
          </div>
        )}
        <Link to="/">
          <Button className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white px-6" data-testid="back-home-btn">
            <Home className="w-4 h-4 mr-2" /> Retour a l'accueil
          </Button>
        </Link>
      </div>
    </PublicLayout>
  );
}
